// libraries
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom"
import {get, startCase, lowerCase, sortBy, get as _get} from "lodash";

// store and utils
import * as actions from "../../../store/actions";
import * as ODCalls from "../../../store/calls/indicativeOptionData";
import {getAssetClasses, getBusinessTypes} from "../../../store/calls";
import validators from "../../../utils/forms/validators";
import formatters from "../../../utils/forms/formatters";
import * as preconfiguredInputs from '../../../utils/forms/preconfiguredInputs'
import api from "../../../store/api";

// components
import FormFlowWrapper from "../../theme/wrappers/FormFlowWrapper";
import FormFlowStepForm from "../../theme/forms/FormFlowStepForm";
import BaseForm from "../../theme/forms/BaseForm";
import Input from "../../theme/forms/Input";
import ChevronSteps from "../../theme/forms/ChevronSteps";
import {
    addErrorAlert,
    toggleGlobalLoader,
} from "../../../store/calls/alertLoadingDispatch";
import SelectByCategory from "../../theme/forms/SelectByCategory";
import InfoFeed from "../../theme/cardlists/infoFeed";
import FormFlow from "../../theme/formFlows/FormFlow";
import TabsSteps from "../../theme/forms/TabsSteps";
import {prepareApplicationFormFlow, updateOrCreateApplication} from "../../../store/calls/applicationData";
import LoansForm from "../../theme/forms/loanAdditionForm";
import FileUploader from "../../theme/forms/FileUploader";
import ApplicationFileUpload from "./ApplicationFileUpload";
import AlertCard from "../../theme/cards/AlertCard";
import TelephoneNumber from "../../subcomponents/TelephoneNumber";

class ApplicationFormFlow extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            currTab: 1,
        };
    }

    componentDidMount() {

        // this process depends a lot on the mountApplication function, which is set from other parts of the app
        // so that they can tell it what we already know and what we need to pull. See store/calls/applicationData/mountApplication
        toggleGlobalLoader(true);

        Promise.allSettled([
            ODCalls.getAssetTypes(),
            ODCalls.getAvailableImprovements(),
        ])
            .catch(() => {
                addErrorAlert("Failed to load all initial data.", 10 * 1000);
            })
            .finally(() => {
                toggleGlobalLoader(false);
            });
    }

    setCurrentTab = (tab) => {
        this.setState({ ...this.state, currTab: tab });
    };


    getFormFlowConfig() {

        return {
            title: "Get Indicative",
            onComplete: () => {
                new Promise(resolve => {
                    this.updateAppInDB();
                    resolve();
                }).then(() => {
                    this.props.history.push('/application/'+ this.props.application.app.id);
                })

            },
            steps: [
                {
                    // step 1
                    title: "Tell us about you",
                    onComplete: () => {
                        if (!this.props.application.app.id) {
                            this.createAppInDB();
                        } else {
                            this.updateAppInDB()
                        }
                    },
                    inputs: [
                        {
                            type: "text",
                            dataKey: "name",
                            label: "Your Name *",
                            inputClasses: "is-medium",
                            ...validators.isStringOfMinLength(2),
                        },
                        {
                            type: "phone",
                            dataKey: "phone",
                            label: "Cell Phone *",
                            inputClasses: "is-medium",
                            placeholder: "(XXX) XXX-XXXX",
                            formatter: formatters.phone.formatter,
                            ...validators.isPhoneNumber
                        },
                        {
                            type: "text",
                            dataKey: "company",
                            label: "Company *",
                            inputClasses: "is-medium",
                        },
                        {
                            type: "text",
                            dataKey: "mailing_address",
                            label: "Mailing Address *",
                            inputClasses: "is-medium",
                            ...validators.isStringOfMinLength(10),
                        },
                        {
                            type: "select",
                            dataKey: "role",
                            label: "Role w/ Respect To Property *",
                            inputClasses: "is-medium is-fullwidth",
                            controlClasses: "is-expanded",
                            placeholder: "--",
                            options: [
                                {
                                    value: 'contractor',
                                    display: 'Contractor / Project Manager'
                                },
                                {
                                    value: 'owner',
                                    display: 'Property Owner'
                                },
                                {
                                    value: 'renter',
                                    display: 'Property Renter'
                                },
                                {
                                    value: 'property_manager',
                                    display: 'Property Manager'
                                },
                                {
                                    value: 'other',
                                    display: 'Other'
                                },
                            ],
                            ...validators.exists,
                        },
                    ],
                },
                // step 2
                {
                    title: "Tell us about the property",
                    onComplete: () => { this.updateAppInDB();},
                    inputs: [
                        {
                            type: "text",
                            dataKey: "owner_name",
                            label: "Name of Owner",
                            subLabel: "Leave blank if you are the owner",
                            inputClasses: "is-medium",
                        },
                        {
                            type: "text",
                            dataKey: "square_footage",
                            label: "How many square feet is the property?",
                            inputClasses: "is-medium",
                            ...validators.isNumber
                        },
                        {
                            type: "text",
                            dataKey: "est_market_value",
                            label: "Estimated Market Value",
                            inputClasses: "is-medium",
                            ...validators.isNumber,
                            ...formatters.USDCurrency
                        },
                        {
                            type: "date",
                            dataKey: "appraisal",
                            label: "Most Recent Appraisal",
                            inputClasses: "is-medium"
                        },
                        {
                            type: "textarea",
                            dataKey: "additional_property_info",
                            label: "Additional Property Information",
                            inputClasses: "is-medium",
                        }


                    ]
                },
                // step 3 project info
                {
                    title: "Update us on the project",
                    onComplete: () => { this.updateAppInDB();},
                    inputs: [
                        {
                                type: "select",
                                dataKey: "project_type",
                                label: "Project Type *",
                                inputClasses: "is-medium is-fullwidth",
                                controlClasses: "is-expanded",
                                placeholder: "--",
                                options: [
                                    {
                                        value: 'retrofit',
                                        display: 'Retrofit'
                                    },
                                    {
                                        value: 'new_construction',
                                        display: 'New Construction'
                                    },
                                    {
                                        value: 'completed_upgrade_refi',
                                        display: 'Refinancing of Completed Upgrades'
                                    }
                                ],
                                ...validators.exists,
                        },
                        {
                            type: "text",
                            dataKey: "project_cost",
                            label: "Project Cost",
                            inputClasses: "is-medium",
                            ...validators.isNumber,
                            ...formatters.USDCurrency,
                        },
                        {
                            type: "date",
                            dataKey: "start_date",
                            label: "Start Date",
                            inputClasses: "is-medium",
                            validator: val => val,
                        },
                        {
                            type: "textarea",
                            dataKey: "project_description",
                            label: "What are your goals in making these improvements?",
                            inputClasses: "is-medium",
                        }


                    ]
                },
                // step 4 Dealbreakers
                {
                    title: "Anything we should know about the property's history?",
                    onComplete: () => {  this.updateAppInDB();},
                    inputs: [
                        {
                            ...preconfiguredInputs.nullBooleanSelect,
                            dataKey: "prop_has_tax_delinquency",
                            label: "Has there been a tax delinquency on the property since purchase? *",
                            inputClasses: "is-medium",
                            ...validators.exists
                        },
                        {
                            ...preconfiguredInputs.nullBooleanSelect,
                            dataKey: "prop_has_mortgage_default",
                            label: "Has there been a payment default on the mortgage in the last three years? *",
                            inputClasses: "is-medium",
                            ...validators.exists
                        },
                        {
                            ...preconfiguredInputs.nullBooleanSelect,
                            dataKey: "prop_is_bankruptcy_asset",
                            label: "Is the parcel currently listed as an asset in bankruptcy? *",
                            inputClasses: "is-medium",
                            ...validators.exists
                        },
                        {
                            ...preconfiguredInputs.nullBooleanSelect,
                            dataKey: "prop_has_involuntary_liens",
                            label: "Are there any involuntary liens recorded on the property?  *",
                            inputClasses: "is-medium",
                            ...validators.exists
                        },
                        {
                            ...preconfiguredInputs.nullBooleanSelect,
                            dataKey: "owner_has_declared_bankruptcy",
                            label: "Has the owner declared bankruptcy in the last five years? *",
                            inputClasses: "is-medium",
                            ...validators.exists
                        },
                        {
                            ...preconfiguredInputs.nullBooleanSelect,
                            dataKey: "prop_has_sba_lien",
                            label: "Is there an SBA lien recorded on the property? *",
                            inputClasses: "is-medium",
                            ...validators.exists
                        },
                        {
                            type: "textarea",
                            dataKey: "prop_history_explanation",
                            label: "Please explain if you answered yes to any of the above",
                            inputClasses: "is-medium",
                        }
                    ]
                },
                // loan stuff
                {
                    title: "Tell us about any loans you might have on the property.",
                    onComplete: () => {  this.updateAppInDB();},
                    component: this.renderLoanInputStep(),
                },
                // upload files
                {
                    title: "Upload Documents",
                    onComplete: () => {  this.updateAppInDB();},
                    component: this.renderDocumentInputStep(),
                },
            ],
        };
    }

    // returns speadable dict to update the value of inputs in the form flow
    updateInputWithDataKey = (dataKey) => {
        return {
            onChange: val => this.props.updateInputs({ [dataKey]: val})
        }
    }



    updateAppInDB = () => {
        let data = {
                id: this.props.application.app.id,
                data: {
                    // ..._get(this.props,'application.app.data', {}),
                    ...this.props.inputs
                }
        };

        return updateOrCreateApplication(data);
    }

    createAppInDB = () => {

        let data = {
            data: {...this.props.inputs},
            indicative: this.props.application.indicative.indicative_id,
            project: this.props.focusedProject,
            user: this.props.userData.user_id
        };

        return updateOrCreateApplication(data)
    }


    renderLoanInputStep = () => {

        const existingLoans = this.props.inputs.loans || [];

        const onLoanSubmit = (loan, index) => {
            let loans = existingLoans;
            if (index === 0 || index) {
                loans[index] = loan;
            } else {
                loans = [...existingLoans, loan]
            }

            loans = sortBy(loans, 'loan_seniority');
            this.props.updateInputs({ loans });
        }

        const onLoanRemove = index => {
            const loans = existingLoans;
            loans.splice(index, 1);
            this.props.updateInputs({ loans })
        }

        return <LoansForm
                loans={this.props.inputs.loans || []}
                onLoanSubmit={onLoanSubmit}
                onLoanRemove={onLoanRemove}
        />
    }

    renderDocumentInputStep = () => {

        const existingFiles = this.props.application.files;

        return (
            <div>

            <ApplicationFileUpload
                defaultFileInputs={[]}
                existingFiles={existingFiles}
            />
        </div>
    )
    }


    renderStepsAndTitle() {
        return (
            <>
                {/** DESKTOP */}
                <h2 className="is-size-2 has-text-primary mb-4 only-desktop">
                    Apply Now for Financing
                </h2>
                <ChevronSteps stepCount={6} activeStep={this.props.currStep} />
                {/** MOBILE */}
                {/* <div className="tabs tabs-heading only-mobile"> */}
                {/*    <ul> */}
                {/*        <li className={this.state.currTab == 1 ? "is-active" : ""}> */}
                {/*            <a onClick={() => this.setCurrentTab(1)}>Get an Indicative</a> */}
                {/*        </li> */}
                {/*        <li className={this.state.currTab == 2 ? "is-active" : ""}> */}
                {/*            <a onClick={() => this.setCurrentTab(2)}>Your Project</a> */}
                {/*        </li> */}
                {/*    </ul> */}
                {/* </div> */}
            </>
        );
    }

    renderMobile() {
        return (
            <div className="container-tabs-formflow only-mobile">
                {this.state.currTab == 1 && (
                    <>
                        <TabsSteps stepCount={5} activeStep={this.props.currStep}/>
                        <FormFlow {...this.getFormFlowConfig()} />
                    </>
                )}
                {this.state.currTab == 2 && (
                    <InfoFeed data={this.props.infoFeedDisplay}>
                        <div className="box has-background-success-dark">
                            <div className="has-text-white-bis is-flex">
                <span className="icon mt-1 mr-5 is-size-3">
                  <i className="fas fa-check-circle"/>
                </span>
                                <span className="is-size-5 has-text-weight-semibold">
                  This property is pace eligible!
                </span>
                            </div>
                        </div>
                    </InfoFeed>
                )}
                <br/>
                <br/>
                <br/>
                <br/>
            </div>
        );
    }

    renderFlow() {
        return (
            <div className="columns only-desktop">
                    <div className="column is-three-fifths">
                        <FormFlow {...this.getFormFlowConfig()} />
                    </div>
                    <div className="column pl-3">
                        <div className="pl-5">
                            <div className="form-section-header">Got Questions?</div>
                            <div className="card mb-3">
                                <header className="card-header">
                                    <p className="card-header-title">
                                        We're available to talk! Call Now:
                                    </p>

                                </header>
                                <div className="card-content">
                                    <TelephoneNumber
                                      className="has-text-success is-size-3 has-text-semibold"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
        );
    }

    render() {

        return (

            <FormFlowWrapper>
                <div className="form-flow-wrapper">
                    {this.renderStepsAndTitle()}
                    {this.renderFlow()}
                </div>
            </FormFlowWrapper>

        );
    }
}

const mapStateToProps = (state) => ({
    inputs: state.formFlow.inputs,
    computed: state.formFlow.computed,
    application: state.applicationData,
    currStep: state.formFlow.currStep,
    infoFeedDisplay: state.infoFeed,
    userData: state.user.decodedTokenData,
    optionData: state.indicativeOptionData,
    focusedProject: state.projectLeads.focusedProject
});

const mapDispatchToProps = (dispatch) => ({
    updateInfoFeedDisplay: (data) => dispatch(actions.infoFeed.updateInfoFeedDisplay(data)),
    updateInputs: (data) => dispatch(actions.formFlow.updateFormFlowInputs(data)),
    setFocusedProject: (data) => dispatch(actions.projectLeads.setFocusedProject(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationFormFlow);
